import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../ui/Button/Button.jsx';
import { createPaymentRecoveryLink, markPaymentRecoveryPromptSeen } from '../../api/paymentRecoveryApi';
import { goToPayPage, isPayPageUrl } from '../../pay/navigation.js';
import './PaymentRecoveryPrompt.less';

const stopTime = (iso) => {
  const at = iso ? new Date(iso) : null;
  if (!at || Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * The card-confirmation prompt on the charging screen (payment recovery).
 *
 * `prompt` is the API's answer (usePaymentRecoveryPrompt). The banner says
 * only what that answer states: a stop time and a remaining amount appear
 * only when `enforcement` is present, because the API sends it only when
 * this session really will be stopped.
 *
 * It does not block the screen: charging goes on and every control below
 * stays usable.
 */
export default function PaymentRecoveryPrompt({ sessionUid, prompt }) {
  const [busy, setBusy] = useState(false);
  const [linkFailed, setLinkFailed] = useState(false);
  // What has already been reported as seen, for which session.
  const reported = useRef({ uid: null, shown: false, warning: false });

  const required = prompt?.required === true;
  const enforcement = required && prompt.enforcement ? prompt.enforcement : null;
  const warning = enforcement?.warning === true;
  const stopping = enforcement?.stopRequested === true;

  // Report the prompt once it is on screen, and once more when it first
  // shows the warning. A failed report shows nothing: staff are alerted
  // server-side exactly because it was not recorded.
  useEffect(() => {
    if (!required || !sessionUid) return;
    if (reported.current.uid !== sessionUid) reported.current = { uid: sessionUid, shown: false, warning: false };
    const first = !reported.current.shown;
    const firstWarning = warning && !reported.current.warning;
    if (!first && !firstWarning) return;
    reported.current.shown = true;
    if (warning) reported.current.warning = true;
    markPaymentRecoveryPromptSeen(sessionUid).catch(() => {});
  }, [required, warning, sessionUid]);

  const confirmCard = useCallback(async () => {
    if (busy || !sessionUid) return;
    setBusy(true);
    setLinkFailed(false);
    const result = await createPaymentRecoveryLink(sessionUid);
    if (result.ok && isPayPageUrl(result.url)) {
      // A full navigation: the payment page boots in its own minimal tree.
      goToPayPage(result.url);
      return;
    }
    setBusy(false);
    setLinkFailed(true);
  }, [busy, sessionUid]);

  if (!prompt) return null;

  if (!required) {
    if (prompt.state !== 'secured') return null;
    return (
      <div className="prp prp--secured" role="status" data-testid="payment-recovery-secured">
        <p className="prp-title">Card confirmed — charging continues</p>
      </div>
    );
  }

  const at = enforcement ? stopTime(enforcement.stopsAt) : null;
  const remaining = enforcement?.remainingBeforeStop?.display || null;
  const tone = stopping || warning ? 'danger' : 'warning';

  let title = 'Confirm your card to keep charging';
  if (stopping) title = 'Charging is being stopped';
  else if (warning) title = 'Charging is about to stop — confirm your card now';

  return (
    <section
      className={`prp prp--${tone}`}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-label="Card confirmation needed"
      data-testid="payment-recovery-prompt"
      data-state={stopping ? 'stopping' : warning ? 'warning' : 'required'}
    >
      <p className="prp-title">{title}</p>
      {stopping ? (
        <p className="prp-body">
          Your card was not confirmed in time, so charging is being stopped. You can still
          pay for this session.
        </p>
      ) : (
        <p className="prp-body">
          {prompt.chargingContinues !== false ? 'Charging continues for now. ' : ''}
          Payment must be completed to continue.
        </p>
      )}
      {enforcement && !stopping && at && (
        <p className="prp-body prp-deadline" data-testid="payment-recovery-deadline">
          Without it, charging stops at {at}
          {remaining ? `, or sooner after a further ${remaining} of charging` : ''}.
        </p>
      )}
      <Button
        className="prp-action"
        fullWidth
        busy={busy}
        onClick={confirmCard}
        data-testid="payment-recovery-confirm"
      >
        Confirm card
      </Button>
      {linkFailed && (
        <p className="prp-body prp-error" role="alert" data-testid="payment-recovery-link-error">
          We could not open the payment page. Please try again.
        </p>
      )}
    </section>
  );
}
